import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import { useAuth } from "../context/AuthContext";

const ADMIN_EMAIL = "doc@eidoc.com.br";

export default function Admin() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  async function loadUsers() {
    setLoading(true);
    setError("");
    try {
      const response = await fetch("/api/admin/users", { credentials: "include", cache: "no-store" });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Não foi possível carregar as contas.");
      setUsers(data.users || []);
    } catch (cause) {
      setError(cause.message || "Não foi possível carregar as contas.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadUsers(); }, []);

  const filteredUsers = useMemo(() => {
    const value = query.trim().toLocaleLowerCase("pt-BR");
    return value ? users.filter((item) => `${item.name} ${item.email}`.toLocaleLowerCase("pt-BR").includes(value)) : users;
  }, [users, query]);

  async function setAccess(item) {
    const nextStatus = item.account_status === "suspended" ? "active" : "suspended";
    setBusy(item.id);
    setError("");
    setNotice("");
    try {
      const response = await fetch(`/api/admin/users/${encodeURIComponent(item.id)}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ account_status: nextStatus }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Não foi possível atualizar o acesso.");
      setUsers((current) => current.map((userItem) => userItem.id === item.id ? { ...userItem, account_status: nextStatus } : userItem));
      setNotice(nextStatus === "suspended" ? "Acesso suspenso. As sessões existentes passam a ser bloqueadas." : "Acesso reativado.");
    } catch (cause) {
      setError(cause.message || "Não foi possível atualizar o acesso.");
    } finally {
      setBusy("");
    }
  }

  async function sendReset(item) {
    setBusy(item.id);
    setError("");
    setNotice("");
    try {
      const response = await fetch(`/api/admin/users/${encodeURIComponent(item.id)}/reset-password`, { method: "POST", credentials: "include" });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Não foi possível enviar as instruções.");
      setNotice(`Instruções para redefinir a senha enviadas para ${item.email}.`);
    } catch (cause) {
      setError(cause.message || "Não foi possível enviar as instruções.");
    } finally {
      setBusy("");
    }
  }

  if (user?.email?.toLowerCase() !== ADMIN_EMAIL) return <Navigate to="/app" replace />;

  return (
    <AppLayout>
      <section className="admin-page">
        <div className="dashboard-heading">
          <div><p className="sobretitulo">Administração</p><h1>Contas profissionais</h1><p>Gerencie acesso e recuperação de senha.</p></div>
          <button className="botao secundario" onClick={loadUsers} disabled={loading}>{loading ? "Atualizando…" : "Atualizar lista"}</button>
        </div>
        {error && <p className="erro" role="alert">{error}</p>}
        {notice && <p className="notice" role="status">{notice}</p>}
        <div className="admin-toolbar"><label htmlFor="admin-user-search">Buscar conta</label><input id="admin-user-search" type="search" placeholder="Nome ou e-mail" value={query} onChange={(event) => setQuery(event.target.value)} /><span>{filteredUsers.length} {filteredUsers.length === 1 ? "conta" : "contas"}</span></div>
        <div className="admin-user-list">
          {loading ? <p className="loading-row">Carregando contas…</p> : filteredUsers.length === 0 ? <p className="empty-queue">Nenhuma conta encontrada.</p> : filteredUsers.map((item) => {
            const protectedAdmin = item.email.toLowerCase() === ADMIN_EMAIL;
            const working = busy === item.id;
            return (
              <article className="admin-user-row" key={item.id}>
                <div className="admin-user-identity"><strong>{item.name || "Sem nome"}</strong><span>{item.email}</span></div>
                <span className={`status ${item.account_status === "suspended" ? "status-paused" : ""}`}>{protectedAdmin ? "Administrador" : item.account_status === "suspended" ? "Acesso suspenso" : "Ativo"}</span>
                <span className="admin-user-verified">{item.email_verified ? "E-mail confirmado" : "E-mail não confirmado"}</span>
                <div className="admin-user-actions">
                  <button className="botao-link" disabled={working} onClick={() => sendReset(item)}>{working ? "Aguarde…" : "Enviar redefinição de senha"}</button>
                  {!protectedAdmin && <button className={`botao-link ${item.account_status === "suspended" ? "" : "destructive-action"}`} disabled={working} onClick={() => setAccess(item)}>{working ? "Aguarde…" : item.account_status === "suspended" ? "Reativar acesso" : "Suspender acesso"}</button>}
                </div>
              </article>
            );
          })}
        </div>
        <p className="clinical-boundary">Suspender uma conta bloqueia sessões existentes e novos acessos à plataforma. Os registros relacionados continuam preservados.</p>
      </section>
    </AppLayout>
  );
}
